import {
  getCampaignPlainTextFromRichText,
  getCampaignRichTextFromPlainText
} from '@/lomavisSharedUi/templates/sections/LomavisCampaignBuilder1/richText';
import type {
  AdCampaignDraft,
  AdManagerMediaFile,
  AdManagerNamedOption,
  AdManagerTargetOption
} from '@/redux/slices/adManager/adManager';
import type {
  LomavisBidStrategy1,
  LomavisBudgetType1
} from '@/lomavisSharedUi/molecules/inputs/LomavisBudgetControls1';
import type {
  LomavisCampaignBuilderLocation1,
  LomavisCampaignBuilderOption1
} from '@/lomavisSharedUi/templates/sections/LomavisCampaignBuilder1';

import {
  defaultPlacements,
  MAX_RADIUS_KM,
  MIN_RADIUS_KM,
  SPECIAL_AD_CATEGORY_MIN_RADIUS_KM,
  rawDataKeys
} from './constants';

const isCountryTargetEntry = (entry: any) => {
  if (!entry || typeof entry !== 'object') return false;
  const rawType = String(entry.raw_data?.type || '').toLowerCase();
  if (rawType === 'country') return true;
  const id = String(entry.id || '').trim();
  return id.length === 2 && /^[a-z]{2}$/i.test(id);
};
import { formatFileSize } from './media';
import type {
  AdManagerDsaDefaults,
  AdManagerFormCopyDefaults,
  AdManagerFormState
} from './types';
import {
  deriveSpecialAdCategoryCountry,
  getRealSpecialAdCategories,
  hasRestrictedSpecialAdCategory,
  isConversionTrackingObjective,
  isPixelTrackingObjective
} from './validation';

const formatAmount = (budgetAmountCents?: number | null) => {
  if (!budgetAmountCents) return '';
  const amount = budgetAmountCents / 100;
  return Number.isInteger(amount) ? String(amount) : amount.toFixed(2);
};

const toBudgetAmountCents = (amount: string) => {
  if (!amount) return null;
  const parsedAmount = Number(amount);
  return Number.isFinite(parsedAmount) ? Math.round(parsedAmount * 100) : null;
};

const translationMarkerPattern = /[\u200B-\u200D\uFEFF]/g;

export const cleanTranslationMarkerText = (value?: string) =>
  String(value || '')
    .replace(translationMarkerPattern, '')
    .trim();

const cleanDsaValue = cleanTranslationMarkerText;

export const clampRadiusKm = (radiusKm: number) =>
  Math.min(MAX_RADIUS_KM, Math.max(MIN_RADIUS_KM, radiusKm));

export const sanitizeRawData = (rawData: any) => {
  if (!rawData || typeof rawData !== 'object') return undefined;

  return rawDataKeys.reduce((acc: Record<string, any>, key) => {
    if (rawData[key] !== undefined && rawData[key] !== null) {
      acc[key] = rawData[key];
    }
    return acc;
  }, {});
};

export const getValidCoordinate = (latitude: any, longitude: any) => {
  const lat = Number(latitude);
  const lng = Number(longitude);

  if (
    !Number.isFinite(lat) ||
    !Number.isFinite(lng) ||
    lat < -90 ||
    lat > 90 ||
    lng < -180 ||
    lng > 180 ||
    (lat === 0 && lng === 0)
  ) {
    return undefined;
  }

  return { lat, lng };
};

export const getNamedOption = (option: any): AdManagerNamedOption | null => {
  if (!option || typeof option !== 'object') return null;

  const id = String(option.id || '').trim();
  const name = String(option.name || '').trim();

  if (!id || !name) return null;

  return { id, name };
};

const toTargetOptionPayload = (
  option: LomavisCampaignBuilderOption1
): AdManagerTargetOption => {
  const rawData = sanitizeRawData((option as any).raw_data);
  return {
    id: String(option.id),
    name: option.name,
    ...(rawData && Object.keys(rawData).length ? { raw_data: rawData } : {})
  };
};

const fromDraftMedia = (mediaFiles: AdManagerMediaFile[] = []) =>
  mediaFiles.map((mediaFile) => ({
    id: mediaFile.image_uuid,
    imageUuid: mediaFile.image_uuid,
    kind: mediaFile.kind,
    meta: formatFileSize(mediaFile.original_filesize_bytes),
    name: mediaFile.name,
    previewUrl: mediaFile.preview_url
  }));

export const createDefaultForm = (
  _locationName = '',
  _dsaDefaults: AdManagerDsaDefaults = {},
  copyDefaults: AdManagerFormCopyDefaults = {}
): AdManagerFormState => {
  return {
    campaignName: '',
    objective: 'OUTCOME_AWARENESS',
    placements: defaultPlacements.map((placement) => ({
      ...placement,
      selected: false
    })),
    locations: [],
    targetCountries: [],
    radiusKm: 30,
    ageRange: [18, 65],
    gender: 'all',
    dsaBeneficiary: '',
    dsaPayor: '',
    languages: [],
    interests: [],
    behaviors: [],
    customAudiences: [],
    pixel: null,
    targetEvent: null,
    specialAdCategories: [],
    specialAdCategoryCountry: [],
    ad: {
      accountName: '',
      sponsoredLabel: copyDefaults.sponsoredLabel || 'Sponsored',
      primaryText: '',
      landingUrl: '',
      cta: 'shop_now'
    },
    selectedMediaFiles: [],
    budgetType: 'daily',
    amount: '',
    startDate: '',
    endDate: '',
    bidStrategy: 'automatic',
    maxBidAmount: ''
  };
};

export const toFormState = (
  draft: AdCampaignDraft | undefined,
  locationName: string,
  pixelOptions: AdManagerNamedOption[] = [],
  pixelsLoaded = false,
  dsaDefaults: AdManagerDsaDefaults = {},
  copyDefaults: AdManagerFormCopyDefaults = {}
): AdManagerFormState => {
  if (!draft) {
    return createDefaultForm(locationName, dsaDefaults, copyDefaults);
  }

  const selectedPlacementIds = new Set(draft.placements || []);
  const radiusKm = clampRadiusKm(draft.radius_km || 30);
  const specialAdCategories = draft.special_ad_categories || [];
  const hasRestrictedSpecialAdCategorySelected =
    hasRestrictedSpecialAdCategory(specialAdCategories);
  const normalizedRadiusKm = hasRestrictedSpecialAdCategorySelected
    ? Math.max(radiusKm, SPECIAL_AD_CATEGORY_MIN_RADIUS_KM)
    : radiusKm;
  const objective = draft.objective || 'OUTCOME_AWARENESS';
  const supportsPixelTracking = isPixelTrackingObjective(objective);
  const draftPixel = supportsPixelTracking ? getNamedOption(draft.pixel) : null;
  const matchingPixel =
    draftPixel &&
    pixelOptions.find((option) => String(option.id) === String(draftPixel.id));
  const dsaBeneficiary =
    cleanDsaValue(draft.dsa_beneficiary) ||
    cleanDsaValue(dsaDefaults.beneficiary);
  const dsaPayor =
    cleanDsaValue(draft.dsa_payor) ||
    cleanDsaValue(dsaDefaults.payor) ||
    dsaBeneficiary;

  const rawTargetLocations = draft.target_locations || [];
  const targetCountries = rawTargetLocations
    .filter((entry) => isCountryTargetEntry(entry))
    .map((entry) => ({
      id: String(entry.id),
      name: entry.name,
      raw_data: sanitizeRawData((entry as any).raw_data)
    }));
  const locations = rawTargetLocations
    .filter((entry) => !isCountryTargetEntry(entry))
    .map((location) => ({
      id: location.id,
      name: location.name,
      coordinate: getValidCoordinate(
        location.coordinate?.lat,
        location.coordinate?.lng
      ),
      radiusKm: normalizedRadiusKm,
      raw_data: location.raw_data
    }));
  return {
    uuid: draft.uuid,
    campaignName:
      cleanTranslationMarkerText(
        draft.name || copyDefaults.newCampaignName || 'New campaign'
      ) || 'New campaign',
    objective,
    placements: defaultPlacements.map((placement) => ({
      ...placement,
      selected: selectedPlacementIds.has(placement.id)
    })),
    locations,
    targetCountries,
    radiusKm: normalizedRadiusKm,
    ageRange: hasRestrictedSpecialAdCategorySelected
      ? [18, 65]
      : [draft.age_min || 18, draft.age_max || 65],
    gender: hasRestrictedSpecialAdCategorySelected
      ? 'all'
      : draft.gender || 'all',
    dsaBeneficiary,
    dsaPayor,
    languages: draft.target_languages || [],
    interests: hasRestrictedSpecialAdCategorySelected
      ? []
      : draft.target_interests || [],
    behaviors: hasRestrictedSpecialAdCategorySelected
      ? []
      : draft.target_behaviors || [],
    customAudiences: draft.target_custom_audiences || [],
    pixel: pixelsLoaded ? matchingPixel || null : draftPixel,
    targetEvent: isConversionTrackingObjective(objective)
      ? getNamedOption(draft.target_event)
      : null,
    specialAdCategories,
    specialAdCategoryCountry:
      getRealSpecialAdCategories(specialAdCategories).length > 0
        ? deriveSpecialAdCategoryCountry({ locations, targetCountries })
        : draft.special_ad_category_country || [],
    ad: {
      accountName: locationName || copyDefaults.defaultAccountName || 'Lomavis',
      sponsoredLabel: copyDefaults.sponsoredLabel || 'Sponsored',
      primaryText: getCampaignRichTextFromPlainText(draft.primary_text || ''),
      landingUrl: draft.landing_url || '',
      cta: draft.cta || 'shop_now'
    },
    selectedMediaFiles: fromDraftMedia(draft.media_files),
    budgetType: (draft.budget_type as LomavisBudgetType1) || 'daily',
    amount: formatAmount(draft.budget_amount_cents),
    startDate: draft.start_date || '',
    endDate: draft.end_date || '',
    bidStrategy: (draft.bid_strategy as LomavisBidStrategy1) || 'automatic',
    maxBidAmount: formatAmount(draft.max_bid_amount_cents)
  };
};

export const toDraftPayload = (
  form: AdManagerFormState,
  locationUuid: string
): Partial<AdCampaignDraft> & {
  location_uuid: string;
  media_image_uuids: string[];
} => {
  const realSpecialAdCategories = getRealSpecialAdCategories(
    form.specialAdCategories
  );
  const hasRestrictedSpecialAdCategorySelected = hasRestrictedSpecialAdCategory(
    form.specialAdCategories
  );
  const radiusKm = hasRestrictedSpecialAdCategorySelected
    ? Math.max(clampRadiusKm(form.radiusKm), SPECIAL_AD_CATEGORY_MIN_RADIUS_KM)
    : clampRadiusKm(form.radiusKm);
  const specialAdCategoryCountry =
    realSpecialAdCategories.length > 0
      ? deriveSpecialAdCategoryCountry({
          locations: form.locations,
          targetCountries: form.targetCountries
        })
      : [];

  return {
    location_uuid: locationUuid,
    name: cleanTranslationMarkerText(form.campaignName),
    objective: form.objective,
    placements: form.placements
      .filter((placement) => placement.selected)
      .map((placement) => placement.id),
    radius_km: radiusKm,
    target_locations: form.targetCountries.length > 0
      ? form.targetCountries.map((country) => {
          const rawData = sanitizeRawData((country as any).raw_data) || {
            type: 'country',
            key: String(country.id)
          };
          return {
            id: String(country.id),
            name: country.name,
            raw_data: rawData
          };
        })
      : form.locations.map((location) => {
          const coordinate = getValidCoordinate(
            location.coordinate?.lat,
            location.coordinate?.lng
          );

          return {
            id: location.id,
            name: location.name,
            ...(coordinate ? { coordinate } : {}),
            radius_km: radiusKm,
            ...(location.raw_data
              ? { raw_data: sanitizeRawData(location.raw_data) }
              : {})
          };
        }),
    target_languages: form.languages.map(toTargetOptionPayload),
    target_interests: hasRestrictedSpecialAdCategorySelected
      ? []
      : form.interests.map(toTargetOptionPayload),
    target_behaviors: hasRestrictedSpecialAdCategorySelected
      ? []
      : form.behaviors.map(toTargetOptionPayload),
    target_custom_audiences: form.customAudiences.map(toTargetOptionPayload),
    pixel:
      isPixelTrackingObjective(form.objective) && form.pixel ? form.pixel : {},
    target_event:
      isConversionTrackingObjective(form.objective) && form.targetEvent
        ? form.targetEvent
        : {},
    special_ad_categories: realSpecialAdCategories,
    special_ad_category_country: specialAdCategoryCountry,
    age_min: hasRestrictedSpecialAdCategorySelected ? 18 : form.ageRange[0],
    age_max: hasRestrictedSpecialAdCategorySelected ? 65 : form.ageRange[1],
    gender: hasRestrictedSpecialAdCategorySelected ? 'all' : form.gender,
    dsa_beneficiary: cleanDsaValue(form.dsaBeneficiary),
    dsa_payor: cleanDsaValue(form.dsaPayor),
    primary_text: getCampaignPlainTextFromRichText(form.ad.primaryText),
    headline: '',
    landing_url: form.ad.landingUrl,
    cta: form.ad.cta,
    budget_type: form.budgetType,
    budget_amount_cents: toBudgetAmountCents(form.amount),
    start_date: form.startDate || null,
    end_date: form.endDate || null,
    bid_strategy: form.bidStrategy,
    max_bid_amount_cents:
      form.bidStrategy === 'manual'
        ? toBudgetAmountCents(form.maxBidAmount)
        : null,
    media_image_uuids: form.selectedMediaFiles
      .map((file) => file.imageUuid || String(file.id))
      .filter(Boolean)
  };
};

export const mapTargetingLocation = (
  item: any
): LomavisCampaignBuilderLocation1 => {
  const rawData = sanitizeRawData(item.raw_data);
  const coordinate = getValidCoordinate(
    rawData?.latitude ?? item.latitude,
    rawData?.longitude ?? item.longitude
  );

  return {
    id: String(item.id),
    name: item.name,
    ...(coordinate ? { coordinate } : {}),
    radiusKm: 30,
    raw_data: rawData
  };
};
